import Insight from "../insightClass";
import React from "react";
import { insightCBAMMedia } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight070 = new Insight(
  "09 October 2025",
  {
    pageTitle:
      "CBAM for offshore wind: an introduction to customs and border processes",
    metaDescription:
      "WeBridge Insights, CBAM for offshore wind: an introduction to customs and border processes",
    keyWords:
      "European Offshore Wind Market development, CBAM for offshore wind: an introduction to customs and border processes",
  },
  {
    src: insightCBAMMedia[0],
    alt: "CBAM",
  },
  "CBAM for offshore wind: an introduction to customs and border processes",
  "The Carbon Border Adjustment Mechanism (CBAM) adds obligations to offshore wind developers when importing steel from non-EU nations.",
  `Steel imports are crucial if European nations are to meet ambitious targets for offshore wind development. In 2024 alone the EU imported over 38 million metric tonnes of steel1 - with around 2 million tonnes dedicated to wind installations (predicted to rise to 4 million tonnes by 2030).`,
  <div>
    <strong>WeBridge Insights</strong>
    <h1>
      CBAM for offshore wind: an introduction to customs and border processes
    </h1>
    <h2>
      The Carbon Border Adjustment Mechanism (CBAM) adds obligations to offshore
      wind developers when importing steel from non-EU nations.
    </h2>
    <p className="article-date">09 October 2025</p>
    <br />

    <p>
      Steel imports are crucial if European nations are to meet{" "}
      <a
        href="https://www.windenergybridge.com/insights/carbon-border-adjustment-mechanism-cbam:-what-does-it-mean-for-importing-offshore-wind-infrastructure-from-china"
        target="_blank"
        rel="noopener noreferrer"
      >
        ambitious targets
      </a>{" "}
      for offshore wind development. In 2024 alone the EU{" "}
      <a
        href="https://www.trade.gov/media/5479"
        target="_blank"
        rel="noopener noreferrer"
      >
        imported
      </a>{" "}
      over 38 million metric tonnes of steel<sup>1</sup> - with around 2 million
      tonnes dedicated to wind installations (predicted to rise to 4 million
      tonnes by 2030).<sup>2</sup>
    </p>
    <p>
      Yet the implementation of the Carbon Border Adjustment Mechanism (CBAM)
      has made the process of steel import more demanding, placing significant
      administrative obligations on offshore wind developers who source steel
      components from non-EU manufacturers.
    </p>
    <p>In summary, EU importers (i.e. the development license owners) must:</p>
    <ul>
      <li>
        Identify which of their imported goods fall under the CBAM obligations
      </li>
      <li>
        Select a methodology for calculating emissions produced in the creation
        of those goods
      </li>
      <li>Declare the quantity of imported material</li>
      <li>Calculate the direct and indirect emissions</li>
      <li>
        Assess if the imported goods have already been subject to emissions
        duties in the country of production or purchase - and declare them if so
      </li>
      <li>Purchase the appropriate amount of CBAM certificates</li>
    </ul>
    <br />
    <p>
      We expand on the key considerations of CBAM customs processes in the
      article below.
    </p>
    <p>But first… </p>

    <h3>Perhaps we can help?</h3>
    <p>
      One of the peculiarities of the CBAM is that the financial obligations
      fall on the importer, but the data obligations are on the producer.
      Collaboration and good communication between both parties is crucial to
      avoid costly mistakes. And we can help. In fact that's where we excel.{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        Here's how
      </a>
      .
    </p>

    <h3>
      What are the import declaration requirements for CBAM covered products?
    </h3>
    <p>
      At this point it's worth noting that the nuances of the CBAM policy are
      still being finalised. That's why it's so important for stakeholders
      throughout the offshore wind production chain to have{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        open dialogue
      </a>{" "}
      and discuss their concerns.
    </p>
    <p>But here's what we know so far.</p>
    <p>
      To import goods covered by the CBAM, importers must be registered and
      authorised as declarants and meet specific reporting requirements. The
      most important of those requirements is calculating and declaring embedded
      emissions, then purchasing the required CBAM certificates to offset those
      emissions. Declarants must also provide information on{" "}
      <a
        href="https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=OJ:L_202402522"
        target="_blank"
        rel="noopener noreferrer"
      >
        CN codes
      </a>
      , country of origin, production site and production route.
    </p>
    <p>
      The CBAM covers many steel and iron-based offshore wind components -
      including monopiles, jacket structures and towers, which fall under
      Chapter 73 of the{" "}
      <a
        href="https://taxation-customs.ec.europa.eu/customs/calculation-customs-duties/customs-tariff/combined-nomenclature_en"
        target="_blank"
        rel="noopener noreferrer"
      >
        Combined Nomenclature (CN)
      </a>{" "}
      categories. Semi-finished steel and major fabricated sections used in
      offshore wind structures are also covered - such as steel plates, tubes,
      lattice masts and pillars. Extensions to what's covered by the CBAM are
      expected by 2030.
    </p>
    <p>
      If offshore wind components are fabricated in China but the steel was
      sourced from elsewhere, the country of origin rules can be complex. But
      typically, origin is where the substantial transformation occurs - i.e.
      the fabrication site.
    </p>
    <p>
      Embedded emissions encompass both direct and indirect emissions. This
      generally means accounting the emissions from steelmaking (either from the
      plant that produced the steel or from hot-rolled coil if sourced) plus any
      assembly emissions. If production spans multiple sites, each emission
      source should be tracked.
    </p>

    <p>
      <b>Direct emissions:</b>
    </p>
    <p>
      These are emissions from the steel production process itself (i.e. the
      fossil fuels used to power the manufacturing process).
    </p>

    <p>
      <b>Indirect emissions:</b>
    </p>
    <p>
      These include emissions from electricity used in manufacturing, transport
      and other related processes. However, indirect emissions in the steel
      industry are{" "}
      <a
        href="https://eu.bellona.org/2025/06/23/a-cbam-without-indirect-emissions-a-half-built-bridge-to-decarbonisation/"
        target="_blank"
        rel="noopener noreferrer"
      >
        exempt
      </a>{" "}
      from reporting during the CBAM transitional phase.<sup>3</sup>
    </p>

    <h3>What are the timeframes for CBAM implementation?</h3>
    <p>
      The CBAM is currently in a transitional phase of implementation - set to
      be fully implemented from January 2026. Here are the current reporting
      requirements for each phase.
    </p>

    <p>
      <b>Transitional phase: October 2023 - December 2025</b>
    </p>
    <ul>
      <li>Quarterly reporting </li>
      <li>Total quantity of goods</li>
      <li>Total embedded emissions</li>
      <li>Emissions price paid in country of origin</li>
    </ul>
    <br />

    <p>
      <b>
        Definitive phase: from 1<sup>st</sup>January 2026
      </b>
    </p>
    <ul>
      <li>Yearly reporting</li>
      <li>Total quantity of goods</li>
      <li>Total embedded emissions (verified by an independent third party)</li>
      <li>Emissions price paid in country of origin</li>
      <li>Total number of CBAM certificates purchased and surrendered</li>
    </ul>
    <br />

    <p>
      Once the CBAM enters its definitive phase, only authorised CBAM declarants
      will be able to import goods covered by the CBAM regulation. Importers
      will need to apply for this status through the CBAM register.
    </p>
    <p>
      How much will it cost? CBAM certificates will mirror the average weekly
      auction price of ETS certificates. However, this duty will be{" "}
      <a
        href="https://mepsinternational.com/gb/en/news/q-a-how-cbam-is-set-to-transform-eu-steel-imports"
        target="_blank"
        rel="noopener noreferrer"
      >
        phased in
      </a>{" "}
      gradually as shown below.<sup>4</sup>
    </p>
    <div className="overflow-x-scroll">
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          margin: "20px 0",
          minWidth: "800px",
        }}
      >
        <caption
          style={{
            captionSide: "top",
            textAlign: "left",
            fontWeight: 600,
            marginBottom: "0.5em",
          }}
        >
          CBAM Free Allowance Phase-Out Schedule (2026–2034)
        </caption>
        <thead>
          <tr className="text-left">
            <th scope="col">Year</th>
            <th scope="col">2026</th>
            <th scope="col">2027</th>
            <th scope="col">2028</th>
            <th scope="col">2029</th>
            <th scope="col">2030</th>
            <th scope="col">2031</th>
            <th scope="col">2032</th>
            <th scope="col">2033</th>
            <th scope="col">2034</th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-left">
            <td>
              <b>Free allowances</b>
            </td>
            <td>97.5%</td>
            <td>95.0%</td>
            <td>90.0%</td>
            <td>77.5%</td>
            <td>51.5%</td>
            <td>39.0%</td>
            <td>26.5%</td>
            <td>14.0%</td>
            <td>0.0%</td>
          </tr>
        </tbody>
      </table>
    </div>
    <br />
    <br />
    <p>However…</p>

    <h3>Will CBAM implementation be delayed?</h3>
    <p>
      It looks{" "}
      <a
        href="https://www.recessary.com/en/news/eu-may-postpone-full-implementation-cbam-until-2027"
        target="_blank"
        rel="noopener noreferrer"
      >
        increasingly likely
      </a>{" "}
      that the full implementation of the CBAM is going to be delayed by one
      full year, until 1<sup>st</sup>January 2027.
      <sup>5</sup>
    </p>

    <h3>CBAM for offshore wind: calculating your position</h3>
    <p>
      Broadly speaking there are two methodologies for calculating your position
      in relation to CBAM imports: the calculations-based approach and the
      measurement-based approach.
    </p>
    <p>
      The <b>calculations-based approach</b> determines emissions from source
      streams using either:
    </p>
    <ul>
      <li>
        activity data (i.e. the amount of electricity or diesel used during
        production) and relevant emissions factors OR
      </li>
      <li>
        a mass-balance approach, estimating emissions from production by
        comparing the emissions content of all the inputs and outputs of the
        production process
      </li>
    </ul>
    <br />
    <p>
      The <b>measurement-based approach</b> measures precise greenhouse gas
      concentrations and flow of flue gases directly at the manufacturing plant
      to accurately determine emissions from the production of a good.
    </p>

    <h3>What's the best method for calculating CBAM emissions? </h3>
    <p>
      In short: it's context dependent. Both the calculation-based and
      measurement-based approaches to CBAM emissions measurement have their pros
      and cons.
    </p>
    <p>
      Typically the calculation-based approach is simpler and less costly - and
      can be well suited to businesses with limited emissions monitoring
      capabilities.
    </p>
    <p>
      With the measurement-based approach, you get precision - which can
      subsequently expose areas in the production process where efficiency can
      be improved. The downside is that it requires significant capital
      expenditure in monitoring equipment and human resource to interpret the
      data.
    </p>
    <p>
      In essence, the calculation-based approach is a good starting point for
      businesses that need to quickly adapt to the CBAM regulations and have
      limited resources. And the measurement-based approach is more suitable for
      businesses that need a high degree of accuracy and want to display a
      strong commitment to emissions reduction.
    </p>

    <h3>What happens if there's no emissions data? </h3>
    <p>
      In the absence of supplier-specific data, the importer will be charged
      based on default values to estimate their emissions liability. These
      default values represent the average emissions for producing one tonne of
      a given product in the country of origin - and are set by the European
      Commission. However, these estimates are inherently conservative and will
      likely leave the importer overpaying compared to if they had access to
      accurate emissions data.
    </p>

    <h3>
      Deducting emissions taxes that have already been charged in the country of
      origin
    </h3>
    <p>
      The CBAM mechanism allows importers to deduct any emissions taxes already
      paid by the exporter in the country of origin.
    </p>

    <h3>Regional variations of the CBAM regulations</h3>
    <p>
      The rigours of CBAM can be challenging to reconcile. To further confuse
      the issue, some countries have their own additions and/or interpretations
      of the regulations.
    </p>
    <p>
      For instance, the Dutch Customs Authorities have published an additional
      reporting requirement for importers of CBAM goods, in which the declarant
      is required to add a "fictitious document code" that establishes the
      veracity of the declaration. Failure to do so will mean the declaration is
      automatically rejected, which could cause clearance delays on imports.
    </p>
    <p>
      The UK is also in the process of implementing its own CBAM regulations.
      This is set to become operative from 1<sup>st</sup>January 2027 and will
      have subtle but important differences from the EU CBAM policies. For
      instance, the EU CBAM is a certificate-based system whereas the UK CBAM
      will function as a tax-based system.
    </p>

    <h3>Integration of the CBAM with existing customs procedures</h3>
    <p>
      At this point, it's worth remembering that transporting goods across
      international boundaries already involves a slew of admin. Beyond their
      CBAM obligations, EU-based importers need to declare:
    </p>
    <ul>
      <li>VAT</li>
      <li>Third country duty</li>
      <li>Anti-dumping duty</li>
      <li>Countervailing duty </li>
      <li>Safeguard duty </li>
      <li>Tarriff rate quotas </li>
    </ul>
    <br />
    <p>
      Note: offshore wind foundations are exempt from anti-dumping duty, however
      towers are not exempt.
    </p>
    <p>
      The key to calculating applicable import duties is knowing which tariff
      codes your goods fall under. Unfortunately the tariff code systems are
      complex and misclassifications are common, with as many as{" "}
      <a
        href="https://www.simplyduty.com/get-hs-codes/"
        target="_blank"
        rel="noopener noreferrer"
      >
        30% of imports
      </a>{" "}
      encountering delays or fines due to shipments being given the wrong code.
      <sup>6</sup>
    </p>
    <p>
      We've written more about this in a{" "}
      <a
        href="https://www.windenergybridge.com/insights/importing-steel-from-china:-duties-and-tariffs-payable"
        target="_blank"
        rel="noopener noreferrer"
      >
        separate article
      </a>
      .
    </p>

    <h3>CBAM: the penalties and risks of non-compliance </h3>
    <p>
      The CBAM obligations are not something you want to get wrong. The
      penalties for non-compliance can be severe - financially, operationally
      and reputationally.
    </p>

    <p>
      <b>Financial penalties:</b>
    </p>
    <p>
      During the transitional phase of the CBAM implementation, fines can reach
      up to €50 per ton of underreported or undeclared emissions. This will rise
      to €85 per tonne in the definitive phase. Higher penalties will apply for
      repeat offenders or severe breaches.
    </p>
    <p>
      Again, we can expect regional variation as prosecutors get more accustomed
      with the CBAM regulations. Already the Netherlands have implemented their
      own set of financial penalties of up to €450,000 or 10% of a declarant's
      annual turnover per underreported or undeclared emission.
    </p>

    <p>
      <b>Operational challenges:</b>
    </p>
    <p>
      Problems with the CBAM declaration can result in goods being held. This
      could seriously impact development projects and even trigger breaches of
      contract and potential legal action.
    </p>

    <p>
      <b>Reputational damage: </b>
    </p>
    <p>
      For steel exporters and foundation fabricators, failure to supply accurate
      emissions data could lead to reputational damage and a loss of business
      with European clients.
    </p>

    <h3>Let's talk?</h3>
    <p>
      CBAM is a rapidly evolving policy. Many of the finer points are yet to be
      confirmed and uncertainty is widespread throughout the offshore wind
      sector. Collaborative discourse among stakeholders is crucial.
    </p>
    <p>
      Get in touch for focused insight on how the CBAM could impact your
      business - and to identify where the commercial opportunities are. As
      experts in connecting European offshore wind developers with global steel
      suppliers, we are uniquely positioned to provide valuable input.{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        Let's talk
      </a>
      .
    </p>

    <h3>You may also be interested in:</h3>
    <p>
      &gt;&gt;{" "}
      <a
        href="https://www.windenergybridge.com/insights/carbon-border-adjustment-mechanism-cbam:-what-does-it-mean-for-importing-offshore-wind-infrastructure-from-china"
        target="_blank"
        rel="noopener noreferrer"
      >
        CBAM: what does it mean for importing offshore wind infrastructure from
        China?
      </a>
    </p>
    <p>
      &gt;&gt; CBAM: regulations and compliance for the offshore wind sector
    </p>

    <h3>References </h3>
    <ol className="list-decimal ml-6">
      <li>
        The International Trade Administration:{" "}
        <a
          href="https://www.trade.gov/media/5479"
          target="_blank"
          rel="noopener noreferrer"
        >
          Steel Trade Balance
        </a>
        .
      </li>
      <li>
        WindEurope:{" "}
        <a
          href="https://www.linkedin.com/posts/windeurope_steel-is-70-of-a-wind-turbines-mass-and-activity-7302732625213878272-Li9b/"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
      </li>
      <li>
        Bellona:{" "}
        <a
          href="https://eu.bellona.org/2025/06/23/a-cbam-without-indirect-emissions-a-half-built-bridge-to-decarbonisation/"
          target="_blank"
          rel="noopener noreferrer"
        >
          A CBAM without indirect emissions? A half-built bridge to
          decarbonisation
        </a>
        .
      </li>
      <li>
        MEPS Internation:{" "}
        <a
          href="https://mepsinternational.com/gb/en/news/q-a-how-cbam-is-set-to-transform-eu-steel-imports"
          target="_blank"
          rel="noopener noreferrer"
        >
          How CBAM will transform EU steel imports
        </a>
        .
      </li>
      <li>
        Reccessary:{" "}
        <a
          href="https://www.reccessary.com/en/news/eu-may-postpone-full-implementation-cbam-until-2027"
          target="_blank"
          rel="noopener noreferrer"
        >
          European Parliament supports CBAM amendments
        </a>
        .
      </li>
      <li>
        Simply Duty:{" "}
        <a
          href="https://www.simplyduty.com/get-hs-codes/"
          target="_blank"
          rel="noopener noreferrer"
        >
          HS Codes
        </a>
        .
      </li>
    </ol>

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
